/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AtMapPin } from '../../../atoms/at-map-pin'
import { MlMapCard, MlMapCardProps } from '../../../molecules/ml-map-card'
import { createMap, createMarker, createMarkerInfoWindow, getLatLongFromAddress } from '../or-map.service'
import { makeElementMarkerOverlayClass, makePopupOverlayClass, PopupOverlayMethods } from './overlays'
import { ZoomButtons } from './zoom'

type MapMarkerPosition = { position: google.maps.LatLngLiteral } | { address: string }
type MapMarkerPopup = { infoWindow?: google.maps.InfoWindowOptions | null } | { cardPopup?: MlMapCardProps | null }
export type MapMarker = MapMarkerPosition & MapMarkerPopup & { onTrackMarkerClick?: () => void }

export interface MapProps {
  zoom?: number
  markers: MapMarker[]
}

export const Map = ({ markers = [], zoom = 15 }: MapProps) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const popupOverlayRef = useRef<PopupOverlayMethods | null>(null)
  const [elementMarkerPortalEls, setElementMarkerPortalEls] = useState<[google.maps.LatLng, HTMLElement][]>([])
  const [cardPortalEl, setCardPortalEl] = useState<HTMLElement | null>(null)
  const [currentCard, setCurrentCard] = useState<{ position: google.maps.LatLng; props: MlMapCardProps } | null>(null)

  useEffect(() => {
    const loader = async () => {
      if (mapRef.current) {
        // There's already a map, do nothing
        return
      }

      if (markers.length < 1) {
        // TODO: Throw an exception?
        return
      }

      const [firstMarker] = markers
      const firstMarkerPosition = await getMarkerPosition(firstMarker)
      mapRef.current = createMap(ref, zoom, firstMarkerPosition)
      if (!mapRef.current) {
        // Something went wrong when creating the map, do nothing
        return
      }

      // Create a new variable because TypeScript wont know if it's non-null inside a closure
      const map = mapRef.current

      const PopupOverlay = makePopupOverlayClass()
      const ElementMarkerOverlay = makeElementMarkerOverlayClass()

      // Setup the popup overlay
      const el = document.createElement('div')
      const popupOverlay = new PopupOverlay(el)
      popupOverlay.setMap(map)
      map.addListener('click', () => setCurrentCard(null))
      setCardPortalEl(el)

      popupOverlayRef.current = popupOverlay

      // Create all the popups, markers and expand the bounds accordingly
      const elementMarkers: [google.maps.LatLng, HTMLElement][] = []
      const bounds = new google.maps.LatLngBounds()
      for (const marker of markers) {
        try {
          const markerPosition = await getMarkerPosition(marker)
          if (!markerPosition) {
            // We can't place a marker if it doesn't have a position or address
            continue
          }

          if ('cardPopup' in marker && marker.cardPopup) {
            const el = document.createElement('div')
            // This ESLint rule warns about the google variable, which should be a-okay
            // eslint-disable-next-line no-loop-func
            el.addEventListener('click', () => {
              if (marker.onTrackMarkerClick) marker.onTrackMarkerClick()

              popupOverlay.setPosition(markerPosition)
              setCurrentCard((prevCard) => {
                const newPosition = new google.maps.LatLng(markerPosition)
                const newProps = marker.cardPopup!
                if (!prevCard) {
                  // Nothing opened right now, show it
                  return { position: newPosition, props: newProps }
                }

                const currentPosition = new google.maps.LatLng(prevCard.position)
                if (currentPosition.equals(newPosition)) {
                  // User clicked the same marker, close the popup
                  return null
                }

                // User clicked a different marker, show it
                return { position: newPosition, props: newProps }
              })
            })

            elementMarkers.push([new google.maps.LatLng(markerPosition), el])
          } else {
            const createdMarker = createMarker(map, markerPosition)
            if ('infoWindow' in marker && marker.infoWindow) {
              const infoWindow = createMarkerInfoWindow(marker.infoWindow)
              createdMarker.addListener('click', () => {
                infoWindow.open({
                  anchor: createdMarker,
                  map,
                })
              })
            }
          }

          bounds.extend(markerPosition)
        } catch (err) {
          // Something went wrong, probably while geocoding the address of the marker
          console.error(err)
        }
      }

      // We want to sort the markers to make sure elements at the bottom of
      // the map get drawn first, and thus cover the markers at the top,
      // giving the illusion of them being actual pins
      const sortedElementMarkers = elementMarkers.sort(([a], [b]) => b.lat() - a.lat())
      for (const [position, el] of sortedElementMarkers) {
        new ElementMarkerOverlay(el, position).setMap(map)
      }

      setElementMarkerPortalEls(sortedElementMarkers)

      const mapEl = mapRef.current.getDiv()
      map.setOptions({
        minZoom: Math.min(11, getBoundsZoomLevel(bounds, { width: mapEl.offsetWidth, height: mapEl.offsetHeight }) - 1),
      })

      if (markers.length > 1) {
        map.fitBounds(bounds, 48)
      }
    }

    loader()

    // TODO: Properly handle updates of these props
  }, [markers, zoom])

  useEffect(() => {
    if (currentCard) {
      requestAnimationFrame(() => popupOverlayRef.current?.panToPopup())
    }
  }, [currentCard])

  const adjustZoomBy = (value: number) => () => mapRef.current?.set('zoom', mapRef.current.get('zoom') + value)

  return (
    <div className="relative w-full h-full">
      <div ref={ref} className="w-full h-full rounded-4" />
      <ZoomButtons onZoomIn={adjustZoomBy(1)} onZoomOut={adjustZoomBy(-1)} />
      {elementMarkerPortalEls.map(([position, el]) =>
        createPortal(
          <AtMapPin title={currentCard?.props?.title} selected={currentCard?.position.equals(position)} />,
          el,
          position.toString()
        )
      )}
      {cardPortalEl && currentCard
        ? createPortal(<MlMapCard {...currentCard.props} />, cardPortalEl, currentCard.position.toString())
        : null}
    </div>
  )
}

const getMarkerPosition = async (marker: MapMarker): Promise<google.maps.LatLng | google.maps.LatLngLiteral | null> => {
  if ('position' in marker && marker.position) {
    return marker.position
  }

  if ('address' in marker && marker.address) {
    return getLatLongFromAddress(marker.address)
  }

  return null
}

const getBoundsZoomLevel = (bounds: google.maps.LatLngBounds, mapSize: { width: number; height: number }) => {
  const WORLD_SIZE = { height: 256, width: 256 }
  const ZOOM_MAX = 21

  const latRad = (lat: number) => {
    const sin = Math.sin((lat * Math.PI) / 180)
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2
  }

  const zoom = (mapPx: number, worldPx: number, fraction: number) => {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2)
  }

  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()

  const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI

  const lngDiff = ne.lng() - sw.lng()
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360

  const latZoom = zoom(mapSize.height, WORLD_SIZE.height, latFraction)
  const lngZoom = zoom(mapSize.width, WORLD_SIZE.width, lngFraction)

  return Math.min(latZoom, lngZoom, ZOOM_MAX)
}
